/**
 * Chat-message HTML sanitizer.
 *
 * Chat messages are produced by a TipTap rich-text composer and stored as
 * HTML, then rendered via dangerouslySetInnerHTML in `MessageItem`. Without
 * sanitization, a user could send `<img src=x onerror=...>` and execute
 * script in every viewer's browser — a one-message account-takeover.
 *
 * The composer applies its own client-side sanitizer at compose time, but
 * that runs in the *sender's* browser and is trivially bypassed by hitting
 * the API directly with crafted HTML. The render-time call here, plus the
 * server-side ingestion sanitizer in `chat-operations.ts`, are the two
 * places we actually trust. Keep the allow-list here in lockstep with the
 * one in `chat-operations.ts` — any divergence is a vulnerability.
 *
 * Allow-list mirrors the TipTap schema in `MessageInput.tsx`: paragraph,
 * inline emphasis, code blocks, ordered/unordered lists, links (with
 * javascript:/data: URIs forbidden by DOMPurify), blockquotes, and
 * mention spans (`<span data-mention data-user-id>`).
 */

import DOMPurify from 'dompurify';

const ALLOWED_TAGS = [
  'p',
  'br',
  'strong',
  'b',
  'em',
  'i',
  'u',
  's',
  'del',
  'strike',
  'code',
  'pre',
  'ul',
  'ol',
  'li',
  'a',
  'blockquote',
  'span',
];

const ALLOWED_ATTR = [
  'href',
  'target',
  'rel',
  'class',
  'data-mention',
  'data-user-id',
  'data-display-name',
];

export function sanitizeChatHtml(dirty: string): string {
  return DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    // Disable the blanket data-* allowance. The three mention data-* attributes
    // we DO want are enumerated in ALLOWED_ATTR above and survive this, because
    // an attribute explicitly listed in ALLOWED_ATTR is permitted regardless of
    // ALLOW_DATA_ATTR; any other data-* attribute is dropped.
    ALLOW_DATA_ATTR: false,
    // NOTE: do NOT add `USE_PROFILES` here. DOMPurify treats USE_PROFILES as
    // authoritative and IGNORES ALLOWED_TAGS / ALLOWED_ATTR when it is set,
    // which silently widens the allow-list (e.g. permits <img>) and strips the
    // mention data-* attributes — the opposite of this file's intent. The
    // explicit lists above are the contract; keep them in lockstep with the
    // server-side allow-list in packages/db-operations/src/chat-operations.ts.
    // DOMPurify's defaults already block javascript: and data: URIs in href,
    // leaving http/https/mailto/relative URLs intact.
  });
}
